import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Renderer2 } from '@angular/core';
import { makeStateKey, TransferState } from '@angular/platform-browser';
import * as Rx from 'rxjs';
import { catchError, finalize, first, map, share, tap } from 'rxjs/operators';
import * as i0 from "@angular/core";
import * as i1 from "@angular/common/http";
import * as i2 from "@angular/platform-browser";
var IconService = /** @class */ (function () {
    function IconService(http, transferState, platformId) {
        this.http = http;
        this.transferState = transferState;
        this.platformId = platformId;
        this.inProgressUrlFetches = new Map();
        this.cachedIconsByUrl = new Map();
    }
    /**
     * Fetch a URL to get an icon SVG string
     *
     * @private
     * @param {string} url The URL to the SVG
     * @returns {string} The SVG markup
     *
     * @memberof IconService
     */
    IconService.prototype.fetchUrl = function (url) {
        var _this = this;
        // Store in-progress fetches to avoid sending a duplicate
        // request for a URL when there is already a request in progress for that
        //  URL. It's necessary to call share() on the Rx.Observable returned by
        // http.get() so that multiple subscribers don't cause multiple XHRs.
        var inProgressUrlFetch$ = this.inProgressUrlFetches.get(url);
        if (inProgressUrlFetch$) {
            return inProgressUrlFetch$;
        }
        var request$ = this.http.get(url, { responseType: 'text' }).pipe(first(), finalize(function () {
            _this.inProgressUrlFetches.delete(url);
        }), share());
        this.inProgressUrlFetches.set(url, request$);
        return request$;
    };
    /**
     * Create an SVG element from an SVG markup
     *
     * @private
     * @param {string} svgString The SVG markup
     * @returns {SVGElement} The SVGElement Node rendered
     *
     * @memberof IconService
     */
    IconService.prototype.createSvgElement = function (markup, renderer) {
        var div = renderer.createElement('div');
        // TODO T37429 Is there a better way than innerHTML? Renderer doesn't appear
        // to have a method for creating an element from an HTML string.
        div.innerHTML = markup;
        var svg = div.querySelector('svg');
        if (!svg) {
            throw Error("<svg> tag not found. Markup is " + markup.slice(0, 100) + ".");
        }
        return svg;
    };
    /**
     * Set the necessary SVG attributes.
     *
     * @private
     * @param {SVGElement} svg The SVG element
     * @memberof IconService
     */
    IconService.prototype.setSvgAttributes = function (svg) {
        if (!svg.getAttribute('xmlns')) {
            svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
        }
        svg.removeAttribute('class');
        svg.setAttribute('fit', '');
        svg.setAttribute('height', '100%');
        svg.setAttribute('width', '100%');
        svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
        // Disable IE11 default behavior to make SVGs focusable.
        svg.setAttribute('focusable', 'false');
        return svg;
    };
    /** Creates a placeholder text node for when svgs aren't loadable */
    IconService.prototype.createPlaceholder = function (renderer, fallback) {
        var div = renderer.createElement('div');
        div.setAttribute('title', fallback);
        renderer.setStyle(div, 'height', '100%');
        renderer.setStyle(div, 'width', '100%');
        renderer.setStyle(div, 'background-color', '#dedede');
        renderer.setStyle(div, 'border-radius', '50%');
        return div;
    };
    /**
     * Get an icon SVG element
     *
     * @param {string} url The URL of the SVG element
     * @param {string} fallback The alt text to display if the URL is unfetchable
     * @returns {SVGElement} The SVG element.
     *
     * @memberof IconService
     */
    IconService.prototype.getIcon = function (url, renderer, fallback) {
        var _this = this;
        var cachedIcon = this.cachedIconsByUrl.get(url);
        if (cachedIcon) {
            return Rx.of(cloneSvg(cachedIcon));
        }
        var serverCacheKey = makeStateKey("ICON_CACHE_" + url);
        var iconHtml$;
        if (isPlatformBrowser(this.platformId) &&
            this.transferState.hasKey(serverCacheKey)) {
            iconHtml$ = Rx.of(this.transferState.get(serverCacheKey, ''));
        }
        else {
            iconHtml$ = this.fetchUrl(url);
        }
        return iconHtml$.pipe(tap(function (markup) {
            if (isPlatformServer(_this.platformId)) {
                _this.transferState.onSerialize(serverCacheKey, function () { return markup; });
            }
        }), map(function (markup) { return _this.createSvgElement(markup, renderer); }), map(function (svg) { return _this.setSvgAttributes(svg); }), tap(function (svg) {
            _this.cachedIconsByUrl.set(url, svg);
        }), map(function (svg) { return cloneSvg(svg); }), catchError(function (err) {
            // put a placeholder for network errors
            if (err instanceof HttpErrorResponse) {
                console.error('Failed to load icon', err.message);
                return Rx.of(_this.createPlaceholder(renderer, fallback));
            }
            throw err;
        }));
    };
    IconService.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function IconService_Factory() { return new IconService(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(i2.TransferState), i0.ɵɵinject(i0.PLATFORM_ID)); }, token: IconService, providedIn: "root" });
    return IconService;
}());
export { IconService };
/** Clones an SVGElement while preserving type information. */
function cloneSvg(svg) {
    return svg.cloneNode(true);
}
